import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Chip,
  Checkbox,
  Card,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DataTable from '../shared/DataTable';
import { formatPriceWithCurrency } from '../../utils/vat';

const STEPS = ['Upload bank statement', 'AI analysis', 'Preview', 'Done'];

// Statuses whose rows can be recorded (checkbox enabled). A 'partial' row is an
// osasuoritus: recorded for its own amount, the invoice stays open with a smaller balance.
const ACTIONABLE = ['match', 'partial', 'amount_mismatch'];

const getOrgId = () => localStorage.getItem('currentOrganizationId');

const formatFiDate = (iso) => {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const formatReference = (ref) => {
  if (!ref) return '';
  // Group digits from the right in blocks of 5 (Finnish viitenumero convention).
  const reversed = ref.split('').reverse().join('');
  const groups = reversed.match(/.{1,5}/g) || [];
  return groups.join(' ').split('').reverse().join('');
};

const BankStatementImportDialog = ({ open, onClose, onCompleted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fileContentBase64, setFileContentBase64] = useState(null);
  const [filename, setFilename] = useState('');

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [commitResult, setCommitResult] = useState(null);

  const resetState = useCallback(() => {
    setCurrentStep(0);
    setLoading(false);
    setError(null);
    setFileContentBase64(null);
    setFilename('');
    setItems([]);
    setSummary(null);
    setSelectedKeys(new Set());
    setCommitResult(null);
  }, []);

  useEffect(() => {
    if (open) resetState();
  }, [open, resetState]);

  // Default-select rows that are safe to record: a full match, and a partial payment
  // (the bank line is exactly what it is; only an over-payment needs a human look).
  const applyPreview = (data) => {
    const previewItems = data.items || [];
    setItems(previewItems);
    setSummary(data.summary || null);
    const defaults = new Set();
    previewItems.forEach((item, idx) => {
      if (item.match_status === 'match' || item.match_status === 'partial') defaults.add(idx);
    });
    setSelectedKeys(defaults);
    setCurrentStep(2);
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setError(null);
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization', getOrgId());

    try {
      const response = await axios.post('/api/invoices/payment-import-parse/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFileContentBase64(response.data.file_content_base64);
      setFilename(response.data.filename || file.name);

      if (response.data.recognized) {
        applyPreview(response.data);
      } else if (response.data.needs_ai_consent) {
        setCurrentStep(1); // consent step
      } else {
        setError(t('Could not read the bank statement'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('Could not read the bank statement'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/xml': ['.xml'],
      'text/xml': ['.xml'],
      'text/plain': ['.txt', '.nda'],
    },
    maxFiles: 1,
    disabled: loading,
  });

  const handleAiConsent = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/invoices/payment-import-parse-ai/', {
        organization: getOrgId(),
        file_content_base64: fileContentBase64,
        filename,
      });
      applyPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('AI analysis failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (idx) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleCommit = async () => {
    const payload = [];
    items.forEach((item, idx) => {
      if (selectedKeys.has(idx) && item.invoice_id && ACTIONABLE.includes(item.match_status)) {
        // The bank line as it is: its amount, its booking date, and its archive id so a
        // re-import of the same file is a no-op rather than a second payment.
        payload.push({
          invoice_id: item.invoice_id,
          paid_date: item.paid_date,
          amount: item.payment?.amount,
          external_ref: item.payment?.archive_id || '',
        });
      }
    });
    if (payload.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/invoices/payment-import-commit/', {
        organization: getOrgId(),
        items: payload,
      });
      setCommitResult(response.data);
      setCurrentStep(3);
    } catch (err) {
      setError(err.response?.data?.error || t('Marking payments failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const statusChip = (status) => {
    const map = {
      match: { label: t('Matched'), color: 'success' },
      partial: { label: t('invoices.recordPayment'), color: 'warning' },
      amount_mismatch: { label: t('Amount mismatch'), color: 'warning' },
      already_paid: { label: t('Already paid'), color: 'default' },
      amili: { label: t('invoices.importAmiliRow'), color: 'info' },
      duplicate_match: { label: t('Multiple matches'), color: 'warning' },
      no_match: { label: t('No match'), color: 'default' },
    };
    const cfg = map[status] || { label: status, color: 'default' };
    return <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />;
  };

  // An Amili-monitored invoice is not recorded here — Amili owns the collection — but the
  // workshop is holding real money. Hand the bank line to the invoice's ohisuoritus dialog
  // with the amount and booking date already filled in.
  const openOhisuoritus = (row) => {
    const params = new URLSearchParams({
      ohisuoritus: row.payment?.amount ?? '',
      date: row.paid_date || '',
    });
    onClose();
    navigate(`/invoices/${row.invoice_id}?${params.toString()}`);
  };

  const ohisuoritusButton = (row) => (
    <Button size="small" variant="outlined" onClick={() => openOhisuoritus(row)} sx={{ whiteSpace: 'nowrap' }}>
      {t('invoices.reportOhisuoritus')}
    </Button>
  );

  const selectedCount = Array.from(selectedKeys).filter(
    (idx) => items[idx] && items[idx].invoice_id && ACTIONABLE.includes(items[idx].match_status)
  ).length;

  const columns = [
    {
      key: 'select',
      label: '',
      width: '1%',
      render: (row) => {
        const idx = row.__idx;
        const actionable = row.invoice_id && ACTIONABLE.includes(row.match_status);
        return (
          <Checkbox
            size="small"
            checked={selectedKeys.has(idx)}
            disabled={!actionable}
            onChange={() => toggleRow(idx)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      key: 'status',
      label: t('Status'),
      width: '1%',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {statusChip(row.match_status)}
          {row.match_status === 'amili' && row.invoice_id && ohisuoritusButton(row)}
        </Box>
      ),
    },
    { key: 'payer', label: t('Payer'), maxWidth: 200, render: (row) => row.payment?.payer || '—' },
    {
      key: 'reference',
      label: t('Reference'),
      noWrap: true,
      render: (row) => formatReference(row.payment?.reference) || '—',
    },
    {
      key: 'amount',
      label: t('Payment amount'),
      align: 'right',
      width: '1%',
      render: (row) => formatPriceWithCurrency(row.payment?.amount),
    },
    {
      key: 'invoice_number',
      label: t('Invoice'),
      width: '1%',
      render: (row) => row.invoice_number || '—',
    },
    {
      key: 'invoice_total',
      label: t('Invoice total'),
      align: 'right',
      width: '1%',
      render: (row) => (row.invoice_total != null ? formatPriceWithCurrency(row.invoice_total) : '—'),
    },
    {
      key: 'open_balance',
      label: t('invoices.openBalance'),
      align: 'right',
      width: '1%',
      render: (row) => (row.open_balance != null ? formatPriceWithCurrency(row.open_balance) : '—'),
    },
    {
      key: 'difference',
      label: t('Difference'),
      align: 'right',
      width: '1%',
      render: (row) => {
        if (row.amount_diff == null || !['amount_mismatch', 'partial'].includes(row.match_status)) return '—';
        return (
          <Typography
            component="span"
            variant="body2"
            sx={{ color: row.match_status === 'partial' ? 'warning.main' : 'error.main', fontWeight: 600 }}
          >
            {formatPriceWithCurrency(row.amount_diff)}
          </Typography>
        );
      },
    },
    {
      key: 'paid_date',
      label: t('Payment date'),
      width: '1%',
      noWrap: true,
      render: (row) => formatFiDate(row.paid_date),
    },
  ];

  // Hide unmatched payments — bank statements are mostly non-invoice lines
  // (outgoing payments, card purchases, fees) that would just bury the matches.
  // Their count stays visible in the summary chip.
  const tableData = items
    .map((item, idx) => ({ ...item, id: idx, __idx: idx }))
    .filter((row) => row.match_status !== 'no_match');

  const renderUploadStep = () => (
    <Box>
      <DialogContentText sx={{ mb: 2 }}>
        {t('Upload a bank statement file to match incoming payments to invoices and mark them as paid.')}
      </DialogContentText>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 5,
          textAlign: 'center',
          cursor: loading ? 'default' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'transparent',
        }}
      >
        <input {...getInputProps()} />
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1">
              {isDragActive ? t('Drop the file here') : t('Drag a bank statement here or click to select')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('Supported: OP, Nordea, Holvi exports and camt.054 (other formats via AI)')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );

  const renderConsentStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('payment_import_ai_consent_question')}
      </Alert>
      <DialogContentText>
        {t('The file {{filename}} will be sent for AI analysis only to find payment dates.', { filename })}
      </DialogContentText>
    </Box>
  );

  const infoRow = (label, value, valueSx) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word', ...valueSx }}>
        {value}
      </Typography>
    </Box>
  );

  const renderMobileCard = (row) => {
    const idx = row.__idx;
    const actionable = row.invoice_id && ACTIONABLE.includes(row.match_status);
    return (
      <Card key={idx} variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {actionable && (
            <Checkbox
              size="small"
              edge="start"
              sx={{ p: 0.5 }}
              checked={selectedKeys.has(idx)}
              onChange={() => toggleRow(idx)}
            />
          )}
          {statusChip(row.match_status)}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatPriceWithCurrency(row.payment?.amount)}
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {infoRow(t('Payer'), row.payment?.payer || '—')}
          {infoRow(t('Reference'), formatReference(row.payment?.reference) || '—')}
          {row.invoice_number && infoRow(t('Invoice'), row.invoice_number)}
          {row.invoice_total != null && infoRow(t('Invoice total'), formatPriceWithCurrency(row.invoice_total))}
          {row.open_balance != null && infoRow(t('invoices.openBalance'), formatPriceWithCurrency(row.open_balance))}
          {['amount_mismatch', 'partial'].includes(row.match_status) &&
            row.amount_diff != null &&
            infoRow(t('Difference'), formatPriceWithCurrency(row.amount_diff), {
              color: row.match_status === 'partial' ? 'warning.main' : 'error.main',
              fontWeight: 600,
            })}
          {infoRow(t('Payment date'), formatFiDate(row.paid_date))}
          {row.match_status === 'amili' && row.invoice_id && (
            <Box sx={{ mt: 1 }}>{ohisuoritusButton(row)}</Box>
          )}
        </Box>
      </Card>
    );
  };

  const renderPreviewStep = () => (
    <Box>
      {summary && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip color="success" label={`${t('Matched')}: ${summary.match || 0}`} />
          {summary.partial > 0 && (
            <Chip color="warning" label={`${t('invoices.recordPayment')}: ${summary.partial}`} />
          )}
          {summary.amount_mismatch > 0 && (
            <Chip color="warning" label={`${t('Amount mismatch')}: ${summary.amount_mismatch}`} />
          )}
          {summary.amili > 0 && <Chip color="info" label={`${t('Amili monitored')}: ${summary.amili}`} />}
          {summary.already_paid > 0 && (
            <Chip label={`${t('Already paid')}: ${summary.already_paid}`} />
          )}
          {summary.duplicate_match > 0 && (
            <Chip color="warning" label={`${t('Multiple matches')}: ${summary.duplicate_match}`} />
          )}
          {summary.no_match > 0 && <Chip label={`${t('Unmatched payments')}: ${summary.no_match}`} />}
        </Box>
      )}
      {summary && summary.amili > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('Amili-monitored invoices are not marked here — handle them as external payments (ohisuoritus).')}
        </Alert>
      )}
      {items.length === 0 ? (
        <Alert severity="warning">{t('No incoming payments found in the file.')}</Alert>
      ) : tableData.length === 0 ? (
        <Alert severity="warning">{t('No payments in this file could be matched to invoices.')}</Alert>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {tableData.map((row) => renderMobileCard(row))}
        </Box>
      ) : (
        <DataTable columns={columns} data={tableData} alwaysVisible horizontalScroll />
      )}
    </Box>
  );

  const renderResultStep = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
      <Typography variant="h6" gutterBottom>
        {t('Marked {{count}} invoices as paid', { count: commitResult?.marked_paid || 0 })}
      </Typography>
      {commitResult?.recorded_partial > 0 && (
        <Typography variant="body1" gutterBottom>
          {t('invoices.importRecordedPartial', { count: commitResult.recorded_partial })}
        </Typography>
      )}
      {commitResult?.already_recorded > 0 && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('invoices.importAlreadyRecorded', { count: commitResult.already_recorded })}
        </Typography>
      )}
      {commitResult?.failed > 0 && (
        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
          {t('{{count}} payments could not be processed.', { count: commitResult.failed })}
        </Alert>
      )}
    </Box>
  );

  const dialogMaxWidth = currentStep === 2 ? 'lg' : 'sm';

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth={dialogMaxWidth} fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('Import payments from bank statement')}
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {currentStep === 0 && renderUploadStep()}
        {currentStep === 1 && renderConsentStep()}
        {currentStep === 2 && renderPreviewStep()}
        {currentStep === 3 && renderResultStep()}
      </DialogContent>
      <DialogActions>
        {currentStep === 1 && (
          <>
            <Button onClick={() => setCurrentStep(0)} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" onClick={handleAiConsent} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : t('Yes, analyze')}
            </Button>
          </>
        )}
        {currentStep === 2 && (
          <>
            <Button onClick={onClose} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleCommit}
              disabled={loading || selectedCount === 0}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                t('invoices.importCommitButton', { count: selectedCount })
              )}
            </Button>
          </>
        )}
        {currentStep === 3 && (
          <Button variant="contained" onClick={onCompleted}>
            {t('Close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BankStatementImportDialog;
